import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getToken, base_url } from "../../config/config";

const BestellungDetails = () => {
  const { id } = useParams();
  const [bestellung, setBestellung] = useState(null);
  const [contactPersons, setContactPersons] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [selectedArticles, setSelectedArticles] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchBestellungDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${base_url}/v1/bestellung/getBestellungById?id=${id}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch Bestellung");
        }

        const data = await response.json();

        if (data.bestellung) {
          setBestellung(data.bestellung);
          const initialSelectedContacts = {};
          data.bestellung.details.forEach((_, index) => {
            initialSelectedContacts[index] = [];
          });
          setSelectedContacts(initialSelectedContacts);
          setSelectedArticles({});

          if (data.bestellung.firmaId?._id) {
            fetchContactPersons(data.bestellung.firmaId._id);
          }
        } else {
          throw new Error("No data found");
        }
      } catch (err) {
        setError(err.message || "Failed to load Bestellung details");
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchTemplates = async () => {
      try {
        const response = await fetch(
          `${base_url}/v1/bestellung/getAllTemplates`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch templates");
        }
        const data = await response.json();
        setTemplates(data.templates || []);
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };

    if (id) {
      fetchBestellungDetails();
      fetchTemplates();
    }
  }, [id]);

  const fetchContactPersons = async (firmaId) => {
    try {
      const response = await fetch(
        `${base_url}/v1/bestellung/getContactPersons?firmaId=${firmaId}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch contact persons");
      }
      const data = await response.json();
      setContactPersons(data.contactPersons || []);
    } catch (err) {
      console.error("Error fetching contact persons:", err);
    }
  };

  const handleContactToggle = (boxIndex, contactId) => {
    setSelectedContacts((prev) => ({
      ...prev,
      [boxIndex]: prev[boxIndex].includes(contactId)
        ? prev[boxIndex].filter((id) => id !== contactId)
        : [...prev[boxIndex], contactId],
    }));
  };

  const handleArticleSelect = (boxIndex, article) => {
    setSelectedArticles((prev) => ({
      ...prev,
      [boxIndex]: article,
    }));
    // Clear template when new article is selected
    setSelectedTemplate(null);
    setTemplateDetails(null);
  };

  const handleSendEmail = async () => {
    if (
      !selectedTemplate ||
      !selectedArticles[0] ||
      !selectedContacts[0]?.length
    ) {
      setError(
        "Please select an article, template, and at least one contact person",
      );
      return;
    }

    try {
      setIsSending(true);
      setError("");
      setSuccessMessage("");

      const response = await fetch(
        `${base_url}/v1/bestellung/sendEmailTemplate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            bestellungId: id,
            templateId: selectedTemplate._id,
            detailId: selectedArticles[0]._id,
            contactPersons: selectedContacts[0],
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      const result = await response.json();
      setSuccessMessage("Email sent successfully!");

      // Clear selections after successful send
      setSelectedTemplate(null);
      setTemplateDetails(null);
      setSelectedArticles({});
      setSelectedContacts({ 0: [] });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      setError(err.message || "Failed to send email");
      console.error("Error sending email:", err);
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-100 py-12 px-4'>
        <div className='max-w-7xl mx-auto'>
          <div className='bg-white rounded-lg shadow-md p-6'>
            <div className='animate-pulse flex flex-col space-y-4'>
              <div className='h-4 bg-gray-200 rounded w-1/4'></div>
              <div className='h-4 bg-gray-200 rounded w-3/4'></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-100 py-12 px-4'>
      <div className='max-w-7xl mx-auto'>
        {/* Header and Navigation */}
        <div className='bg-white rounded-lg shadow-md p-6 mb-6'>
          <div className='flex justify-between items-center'>
            <div>
              <Link
                to='/bestellung'
                className='text-blue-600 hover:text-blue-800 flex items-center mb-4'>
                ← Back to Bestellung List
              </Link>
              <h1 className='text-2xl font-bold text-gray-900'>
                Article Assignment
              </h1>
            </div>
            <div className='text-right'>
              <p className='text-gray-600'>Bestellung ID: {bestellung?._id}</p>
              <p className='text-gray-900 font-medium mt-1'>
                {bestellung?.firmaId?.bezeichnung}
              </p>
              <p className='text-gray-600'>
                Account: {bestellung?.firmaId?.konto}
              </p>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {(error || successMessage) && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              error ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"
            }`}>
            {error || successMessage}
          </div>
        )}

        {/* Article List and Box Container */}
        <div className='flex gap-6'>
          {/* Articles Column */}
          <div className='w-1/4 bg-white rounded-lg shadow-md p-6'>
            <h2 className='text-xl font-bold mb-4'>Articles</h2>
            <div className='space-y-3'>
              {bestellung?.details.map((article) => (
                <div
                  key={article._id}
                  className={`p-3 border rounded-lg hover:bg-gray-50 cursor-pointer ${
                    selectedArticles[0]?._id === article._id
                      ? "border-blue-500 bg-blue-50"
                      : ""
                  }`}
                  onClick={() => handleArticleSelect(0, article)}>
                  <p className='font-medium'>{article.artikelNumber}</p>
                  <p className='text-sm text-gray-600'>{article.description}</p>
                  <p className='text-sm text-gray-500'>
                    {article.menge} {article.unit}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className='flex-1'>
            {/* Article Box */}
            <div className='bg-white rounded-lg shadow-md p-6 mb-6'>
              <div className='flex justify-between items-start mb-6'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Article Assignment
                </h3>
                <div className='flex items-center gap-4'>
                  <select
                    className='border rounded-lg p-2 text-gray-700 bg-gray-50'
                    onChange={async (e) => {
                      const templateId = e.target.value;
                      if (templateId && selectedArticles[0]) {
                        try {
                          const response = await fetch(
                            `${base_url}/v1/bestellung/getBestellungTemplate?bestellungId=${id}&detailId=${selectedArticles[0]._id}&templateId=${templateId}`,
                          );
                          if (!response.ok)
                            throw new Error("Failed to fetch template details");
                          const data = await response.json();
                          setTemplateDetails(data);
                          setSelectedTemplate(
                            templates.find((t) => t._id === templateId),
                          );
                        } catch (err) {
                          console.error(
                            "Error fetching template details:",
                            err,
                          );
                          setError("Failed to load template details");
                        }
                      } else {
                        setTemplateDetails(null);
                        setSelectedTemplate(null);
                      }
                    }}
                    value={selectedTemplate?._id || ""}
                    disabled={!selectedArticles[0]}>
                    <option value=''>Select Template</option>
                    {templates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {selectedTemplate && (
                    <div className='flex gap-2'>
                      <button
                        onClick={() => setIsTemplateModalOpen(true)}
                        className='px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-600 rounded-lg hover:bg-blue-50'>
                        Preview Template
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTemplate(null);
                          setTemplateDetails(null);
                          setIsTemplateModalOpen(false);
                        }}
                        className='text-gray-600 hover:text-gray-800'>
                        Clear Template
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {selectedArticles[0] && (
                <div className='bg-blue-50 rounded-lg p-4 mb-6'>
                  <h4 className='font-medium text-blue-900 mb-2'>
                    Selected Article Details
                  </h4>
                  <div className='space-y-2'>
                    <p className='text-blue-800'>
                      <span className='font-medium'>Description:</span>{" "}
                      {selectedArticles[0].description}
                    </p>
                    <p className='text-blue-800'>
                      <span className='font-medium'>Quantity:</span>{" "}
                      {selectedArticles[0].menge} {selectedArticles[0].unit}
                    </p>
                    <p className='text-blue-800'>
                      <span className='font-medium'>Anr:</span>{" "}
                      {selectedArticles[0].artikelNumber}
                    </p>
                    <p className='text-blue-800'>
                      <span className='font-medium'>Article Number:</span>{" "}
                      {selectedArticles[0].artikelNumber}
                    </p>
                  </div>
                </div>
              )}

              {/* Contact Persons */}
              <div>
                <h4 className='font-medium text-gray-900 mb-3'>
                  Select Contact Persons
                </h4>
                <div className='grid grid-cols-2 gap-4'>
                  {contactPersons.map((person) => (
                    <label
                      key={person._id}
                      className='flex items-center p-3 rounded border hover:bg-gray-50 cursor-pointer transition-colors'>
                      <input
                        type='checkbox'
                        checked={selectedContacts[0]?.includes(person._id)}
                        onChange={() => handleContactToggle(0, person._id)}
                        className='h-4 w-4 text-blue-600 rounded border-gray-300 mr-3'
                      />
                      <div className='flex-1'>
                        <p className='text-gray-900 font-medium'>
                          {person.name}
                        </p>
                        <p className='text-gray-500 text-sm'>{person.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Send Email Button - Fixed at bottom */}
        <div className='fixed bottom-0 left-0 right-0 bg-white shadow-md p-4'>
          <div className='max-w-7xl mx-auto'>
            <button
              onClick={handleSendEmail}
              disabled={
                !selectedTemplate ||
                !selectedArticles[0] ||
                !selectedContacts[0]?.length ||
                isSending
              }
              className={`w-full py-3 px-4 rounded-lg text-lg font-medium transition-colors
                ${
                  !selectedTemplate ||
                  !selectedArticles[0] ||
                  !selectedContacts[0]?.length ||
                  isSending
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                }`}>
              {isSending ? "Sending..." : "Send Email"}
            </button>
          </div>
        </div>

        {/* Template Preview Modal */}
        {isTemplateModalOpen && (
          <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4'>
            <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden'>
              {/* Modal Header */}
              <div className='px-6 py-4 border-b flex justify-between items-center'>
                <h3 className='text-xl font-semibold text-gray-900'>
                  Template Preview - {templateDetails?.template?.subject}
                </h3>
                <button
                  onClick={() => setIsTemplateModalOpen(false)}
                  className='text-gray-500 hover:text-gray-700'>
                  <svg
                    className='w-6 h-6'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div
                className='p-6 overflow-y-auto'
                style={{ maxHeight: "calc(90vh - 120px)" }}>
                {templateDetails?.template?.html ? (
                  <div
                    className='border rounded-lg overflow-hidden'
                    dangerouslySetInnerHTML={{
                      __html: templateDetails.template.html,
                    }}
                  />
                ) : (
                  <div className='text-gray-500 text-center py-8'>
                    No template content available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BestellungDetails;
